import React, { useState } from 'react';
import { Zap, Plus, Settings, Globe, Mail, Database, Cloud, MessageSquare, Check, X } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppSwitch } from '../components/base/AppSwitch';
import { AppBadge } from '../components/base/AppBadge';
import { Toast } from '../components/Toast';
interface Action {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  category: 'automation' | 'notification' | 'data' | 'communication';
}
interface Integration {
  id: string;
  name: string;
  description: string;
  icon: any;
  connected: boolean;
  category: 'crm' | 'email' | 'storage' | 'communication' | 'web';
}
interface ServiceInteraction {
  id: string;
  serviceName: string;
  description: string;
  enabled: boolean;
  direction: 'send' | 'receive' | 'bidirectional';
}
export function ActionsPage() {
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Mock data
  const [actions, setActions] = useState<Action[]>([{
    id: 'a1',
    name: 'Invio email automatico',
    description: 'Invia email automatiche in risposta a trigger specifici',
    enabled: true,
    category: 'automation'
  }, {
    id: 'a2',
    name: 'Creazione task',
    description: 'Crea automaticamente task in base alle conversazioni',
    enabled: true,
    category: 'automation'
  }, {
    id: 'a3',
    name: 'Notifiche push',
    description: 'Invia notifiche push agli utenti',
    enabled: false,
    category: 'notification'
  }, {
    id: 'a4',
    name: 'Estrazione dati',
    description: 'Estrae e struttura dati dalle conversazioni',
    enabled: true,
    category: 'data'
  }, {
    id: 'a5',
    name: 'Risposta automatica',
    description: 'Risponde automaticamente a domande frequenti',
    enabled: true,
    category: 'communication'
  }]);
  const [integrations, setIntegrations] = useState<Integration[]>([{
    id: 'i1',
    name: 'Salesforce',
    description: 'Sincronizza dati con Salesforce CRM',
    icon: Database,
    connected: true,
    category: 'crm'
  }, {
    id: 'i2',
    name: 'Outlook',
    description: 'Integrazione con Microsoft Outlook per email',
    icon: Mail,
    connected: true,
    category: 'email'
  }, {
    id: 'i3',
    name: 'Google Drive',
    description: 'Accesso e archiviazione documenti su Google Drive',
    icon: Cloud,
    connected: false,
    category: 'storage'
  }, {
    id: 'i4',
    name: 'Slack',
    description: 'Invia notifiche e messaggi su Slack',
    icon: MessageSquare,
    connected: false,
    category: 'communication'
  }, {
    id: 'i5',
    name: 'Web API',
    description: 'Connessione a API web personalizzate',
    icon: Globe,
    connected: true,
    category: 'web'
  }]);
  const [serviceInteractions, setServiceInteractions] = useState<ServiceInteraction[]>([{
    id: 's1',
    serviceName: 'Service 2',
    description: 'Riceve dati elaborati da Service 2',
    enabled: true,
    direction: 'receive'
  }, {
    id: 's2',
    serviceName: 'Service 3',
    description: 'Invia richieste di analisi a Service 3',
    enabled: true,
    direction: 'send'
  }, {
    id: 's3',
    serviceName: 'Service 5',
    description: 'Scambio bidirezionale di informazioni',
    enabled: false,
    direction: 'bidirectional'
  }]);
  const handleActionToggle = (actionId: string) => {
    setActions(prev => prev.map(action => action.id === actionId ? {
      ...action,
      enabled: !action.enabled
    } : action));
    setToast({
      message: 'Azione aggiornata',
      type: 'success'
    });
  };
  const handleIntegrationToggle = (integrationId: string) => {
    setIntegrations(prev => prev.map(integration => integration.id === integrationId ? {
      ...integration,
      connected: !integration.connected
    } : integration));
    setToast({
      message: 'Integrazione aggiornata',
      type: 'success'
    });
  };
  const handleServiceInteractionToggle = (interactionId: string) => {
    setServiceInteractions(prev => prev.map(interaction => interaction.id === interactionId ? {
      ...interaction,
      enabled: !interaction.enabled
    } : interaction));
    setToast({
      message: 'Interazione aggiornata',
      type: 'success'
    });
  };
  const getCategoryBadge = (category: string) => {
    const badges: Record<string, {
      label: string;
      variant: any;
    }> = {
      automation: {
        label: 'Automazione',
        variant: 'blue'
      },
      notification: {
        label: 'Notifica',
        variant: 'purple'
      },
      data: {
        label: 'Dati',
        variant: 'green'
      },
      communication: {
        label: 'Comunicazione',
        variant: 'amber'
      },
      crm: {
        label: 'CRM',
        variant: 'blue'
      },
      email: {
        label: 'Email',
        variant: 'purple'
      },
      storage: {
        label: 'Storage',
        variant: 'green'
      },
      web: {
        label: 'Web',
        variant: 'gray'
      }
    };
    const badge = badges[category] || {
      label: category,
      variant: 'gray'
    };
    return <AppBadge variant={badge.variant} data-id="element-928">{badge.label}</AppBadge>;
  };
  const getDirectionIcon = (direction: string) => {
    switch (direction) {
      case 'send':
        return '→';
      case 'receive':
        return '←';
      case 'bidirectional':
        return '⇄';
      default:
        return '';
    }
  };
  return <div className="pb-12 space-y-8" data-id="element-929">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-930" />}

      {/* Header */}
      <div data-id="element-931">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-932">
          Actions
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-933">
          Configura azioni, integrazioni e interazioni tra servizi
        </p>
      </div>

      {/* Service Instructions */}
      <AppCard data-id="element-934">
        <div className="flex items-start justify-between mb-6" data-id="element-935">
          <div data-id="element-936">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-937">
              Istruzioni Service
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-938">
              Definisci le istruzioni operative per questo service
            </p>
          </div>
          <AppButton variant="secondary" size="sm" icon={<Settings className="w-4 h-4" data-id="element-940" />} data-id="element-939">
            Modifica
          </AppButton>
        </div>

        <div className="bg-gray-50 dark:bg-dark-subtle rounded-xl p-4 border border-gray-200 dark:border-dark-medium" data-id="element-941">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed" data-id="element-942">
            Questo service è configurato per rispondere alle richieste degli
            utenti in modo professionale e accurato. Utilizza le integrazioni
            disponibili per accedere a dati esterni e può interagire con altri
            service per fornire risposte complete.
          </p>
        </div>
      </AppCard>

      {/* Actions */}
      <AppCard data-id="element-943">
        <div className="flex items-start justify-between mb-6" data-id="element-944">
          <div data-id="element-945">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-946">
              Azioni Disponibili
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-947">
              Abilita o disabilita le azioni che il service può eseguire
            </p>
          </div>
          <AppButton variant="secondary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-949" />} data-id="element-948">
            Aggiungi azione
          </AppButton>
        </div>

        <div className="space-y-3" data-id="element-950">
          {actions.map(action => <div key={action.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl border border-gray-200 dark:border-dark-medium hover:border-gray-300 dark:hover:border-gray-600 transition-colors" data-id="element-951">
              <div className="flex items-start gap-4 flex-1" data-id="element-952">
                <div className="p-2 bg-white dark:bg-dark-surface rounded-lg" data-id="element-953">
                  <Zap className="w-5 h-5 text-blue-500" data-id="element-954" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-955">
                  <div className="flex items-center gap-2 mb-1" data-id="element-956">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-957">
                      {action.name}
                    </h3>
                    {getCategoryBadge(action.category)}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-958">
                    {action.description}
                  </p>
                </div>
              </div>
              <AppSwitch checked={action.enabled} onChange={() => handleActionToggle(action.id)} data-id="element-959" />
            </div>)}
        </div>
      </AppCard>

      {/* Integrations */}
      <AppCard data-id="element-960">
        <div className="flex items-start justify-between mb-6" data-id="element-961">
          <div data-id="element-962">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-963">
              Integrazioni
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-964">
              Connetti il service a piattaforme esterne
            </p>
          </div>
          <AppButton variant="secondary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-966" />} data-id="element-965">
            Aggiungi integrazione
          </AppButton>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-967">
          {integrations.map(integration => {
          const Icon = integration.icon;
          return <div key={integration.id} className={`p-4 rounded-xl border-2 transition-all ${integration.connected ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-dark-subtle border-gray-200 dark:border-dark-medium'}`} data-id="element-968">
                <div className="flex items-start justify-between mb-3" data-id="element-969">
                  <div className="flex items-center gap-3" data-id="element-970">
                    <div className={`p-2 rounded-lg ${integration.connected ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-white dark:bg-dark-surface'}`} data-id="element-971">
                      <Icon className={`w-5 h-5 ${integration.connected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-972" />
                    </div>
                    <div data-id="element-973">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-974">
                        {integration.name}
                      </h3>
                      {getCategoryBadge(integration.category)}
                    </div>
                  </div>
                  {integration.connected ? <Check className="w-5 h-5 text-green-500" data-id="element-975" /> : <X className="w-5 h-5 text-gray-400" data-id="element-976" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3" data-id="element-977">
                  {integration.description}
                </p>
                <AppButton variant={integration.connected ? 'secondary' : 'primary'} size="sm" fullWidth onClick={() => handleIntegrationToggle(integration.id)} data-id="element-978">
                  {integration.connected ? 'Disconnetti' : 'Connetti'}
                </AppButton>
              </div>;
        })}
        </div>
      </AppCard>

      {/* Service Interactions */}
      <AppCard data-id="element-979">
        <div className="flex items-start justify-between mb-6" data-id="element-980">
          <div data-id="element-981">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-982">
              Interazioni tra Service
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-983">
              Configura come questo service interagisce con altri service
            </p>
          </div>
          <AppButton variant="secondary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-985" />} data-id="element-984">
            Aggiungi interazione
          </AppButton>
        </div>

        <div className="space-y-3" data-id="element-986">
          {serviceInteractions.map(interaction => <div key={interaction.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl border border-gray-200 dark:border-dark-medium hover:border-gray-300 dark:hover:border-gray-600 transition-colors" data-id="element-987">
              <div className="flex items-center gap-4 flex-1" data-id="element-988">
                <div className="text-2xl" data-id="element-989">
                  {getDirectionIcon(interaction.direction)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-990">
                  <div className="flex items-center gap-2 mb-1" data-id="element-991">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-992">
                      {interaction.serviceName}
                    </h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-993">
                      {interaction.direction === 'send' && '(Invio)'}
                      {interaction.direction === 'receive' && '(Ricezione)'}
                      {interaction.direction === 'bidirectional' && '(Bidirezionale)'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-994">
                    {interaction.description}
                  </p>
                </div>
              </div>
              <AppSwitch checked={interaction.enabled} onChange={() => handleServiceInteractionToggle(interaction.id)} data-id="element-995" />
            </div>)}
        </div>
      </AppCard>
    </div>;
}